/**
 * @file OpenTelemetry Metrics Setup
 * @description This module configures OpenTelemetry metrics collection,
 * exporting them to the OpenTelemetry collector in OTLP format.
 */

import { metrics } from '@opentelemetry/api';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import packageJson from '../../../package.json';
import { WEBUI_VERSION } from '../../constants/App';
import { Resource } from '@opentelemetry/resources';
import { SEMRESATTRS_SERVICE_NAME, SEMRESATTRS_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';

/**
 * @constant {OTLPMetricExporter} metricExporter
 * @description Configures an OpenTelemetry metric exporter that sends data via HTTP in OTLP format.
 */
const metricExporter = new OTLPMetricExporter({
    url: 'https://ot.ezlo.com/metrics',
    headers: {},
    keepAlive: true,
});
/**
 * @constant {PeriodicExportingMetricReader} metricReader
 * @description Reads and exports metrics periodically to the configured OTLP endpoint.
 */
const metricReader = new PeriodicExportingMetricReader({
    exporter: metricExporter,
    exportIntervalMillis: 30000,
});

/**
 * @constant {MeterProvider} meterProvider
 * @description Configures the OpenTelemetry MeterProvider with a resource and metric readers.
 */
const meterProvider = new MeterProvider({
    readers: [metricReader],
    resource: Resource.default().merge(
        new Resource({
            [SEMRESATTRS_SERVICE_NAME]: packageJson.name,
            [SEMRESATTRS_SERVICE_VERSION]: WEBUI_VERSION,
        }),
    ),
});

/**
 * @description Sets the global metrics provider for OpenTelemetry.
 */
metrics.setGlobalMeterProvider(meterProvider);

/**
 * @constant {import('@opentelemetry/api').Meter} httpsMeter
 * @description A meter instance used to create and record HTTP-related metrics.
 */
const httpsMeter = metrics.getMeter('https-metrics');

/**
 * @constant {import('@opentelemetry/api').Histogram} httpRequestDuration
 * @description Measures the duration of HTTP requests in milliseconds.
 */
export const httpRequestDuration = httpsMeter.createHistogram('https.request.duration', {
    description: 'Duration of HTTP requests. unit: ms',
    unit: 'ms',
});
/**
 * @constant {import('@opentelemetry/api').Histogram} httpResponseSize
 * @description Measures the size of HTTP responses in bytes.
 */
export const httpResponseSize = httpsMeter.createHistogram('https.response.size', {
    description: 'Size of HTTP responses. unit: bytes',
    unit: 'bytes',
});
/**
 * @constant {import('@opentelemetry/api').Counter} httpStatusCodeCounter
 * @description Counts occurrences of HTTP response status codes.
 */
export const httpStatusCodeCounter = httpsMeter.createCounter('https.response.status_code', {
    description: 'HTTP response status codes',
});
/**
 * @constant {import('@opentelemetry/api').Counter} httpRequestCounter
 * @description Counts the total number of HTTP requests made.
 */
export const httpRequestCounter = httpsMeter.createCounter('https.request.count', {
    description: 'HTTP Request',
});

/**
 * @constant {import('@opentelemetry/api').Meter} wsMeter
 * @description A meter instance used to create and record WS-related metrics.
 */
const wsMeter = metrics.getMeter('ws-metrics');

/**
 * Histogram for measuring the duration of WebSocket requests in milliseconds.
 * @type {import('@opentelemetry/api').Histogram}
 */
export const wsRequestDuration = wsMeter.createHistogram('ws.request.duration', {
    description: 'Duration of WS requests. unit: ms',
    unit: 'ms',
});
/**
 * Histogram for measuring the size of WebSocket responses in bytes.
 * @type {import('@opentelemetry/api').Histogram}
 */
export const wsResponseSize = wsMeter.createHistogram('ws.response.size', {
    description: 'Size of WS responses. unit: bytes',
    unit: 'bytes',
});
/**
 * Counter for tracking WebSocket response statuses.
 * @type {import('@opentelemetry/api').Counter}
 */
export const wsStatusCounter = httpsMeter.createCounter('ws.response.status', {
    description: 'WS response status',
});
/**
 * Histogram for measuring the size of WebSocket broadcast messages in bytes.
 * @type {import('@opentelemetry/api').Histogram}
 */
export const wsBroadcastSize = wsMeter.createHistogram('ws.broadcast.size', {
    description: 'Size of WS broadcast. unit: bytes',
    unit: 'bytes',
});
